import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Tables } from "@/integrations/supabase/types"
import { toast } from "sonner"
import { useAuth } from "./useAuth"
import {
  getLocalVisitors,
  createLocalVisitor,
  updateLocalVisitorExit,
  getLocalVisitorsByDateRange,
  getLocalVisitorByDocument,
  searchLocalVisitors
} from "@/lib/db/repos"

type Visitor = Tables<"visitors">
type Profile = Tables<"profiles">
type Unit = Tables<"units">

type VisitorWithDetails = Visitor & {
  unit: Unit | null
  authorized_by_user: Profile | null
}

export const useVisitors = () => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["visitors", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return []
      const data = await getLocalVisitors()
      return data as VisitorWithDetails[]
    },
    enabled: !!profile?.condominium_id
  })
}

export const useCreateVisitor = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async (data: Omit<Visitor, "id" | "created_at" | "condominium_id">) => {
      if (!profile?.condominium_id) throw new Error("Usuário sem condomínio associado")

      const visitor = await createLocalVisitor({
        ...data,
        condominium_id: profile.condominium_id,
        authorized_by: profile.id
      })
      return visitor
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["visitors"] })
      toast.success("Visitante registrado com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao registrar visitante:", error)
      toast.error("Erro ao registrar visitante")
    }
  })
}

export const useUpdateVisitorExit = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (visitorId: string) => {
      const data = await updateLocalVisitorExit(visitorId)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["visitors"] })
      toast.success("Saída registrada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao registrar saída:", error)
      toast.error("Erro ao registrar saída")
    }
  })
}

export const useVisitorsByDateRange = (startDate: string | null, endDate: string | null) => {
  const { profile } = useAuth()

  return useQuery({
    queryKey: ["visitors-history", profile?.condominium_id, startDate, endDate],
    queryFn: async () => {
      if (!profile?.condominium_id || !startDate || !endDate) return []
      const data = await getLocalVisitorsByDateRange(startDate, endDate)
      return data as VisitorWithDetails[]
    },
    enabled: !!profile?.condominium_id && !!startDate && !!endDate
  })
}

export const useVisitorByDocument = (document: string, condominiumId: string | null) => {
  return useQuery({
    queryKey: ["visitor-by-document", document, condominiumId],
    queryFn: async () => {
      if (!document || document.length < 5 || !condominiumId) return null
      const data = await getLocalVisitorByDocument(document, condominiumId)
      return data
    },
    enabled: !!document && document.length >= 5 && !!condominiumId
  })
}

export type VisitorSuggestion = {
  id: string
  name: string
  document: string | null
  photo_url: string | null
  notes: string | null
  created_at: string
  unit?: {
    id: string
    number: string
    building: string | null
  } | null
}

export const useVisitorSearch = (searchTerm: string, condominiumId: string | null) => {
  const term = searchTerm.trim()
  const enabled = !!condominiumId && term.length >= 3

  return useQuery({
    queryKey: ["visitor-search", condominiumId, term],
    queryFn: async (): Promise<VisitorSuggestion[]> => {
      if (!enabled) return []
      const data = await searchLocalVisitors(term, condominiumId!)
      return data as VisitorSuggestion[]
    },
    enabled,
    staleTime: 30_000,
  })
}
